import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../i18n';
import { contactTranslations, DEPARTMENT_OPTIONS } from '../lib/i18n/contact';
import { validateEmail, validateName, validateMessage, isSpamSubmission } from '../utils/validation';
import { CheckCircle, Send, ChevronDown } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  department: string;
  otherSubject: string;
  message: string;
  honeypot: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  department?: string;
  otherSubject?: string;
  message?: string;
  general?: string;
}

export default function ContactForm() {
  const { lang } = useI18n();
  const t = contactTranslations[lang] || contactTranslations.en;
  const isRtl = lang === 'ar';

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    department: '',
    otherSubject: '',
    message: '',
    honeypot: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [startTime] = useState(Date.now());

  const isOther = formData.department === 'other';

  const resolvedSubject = isOther
    ? formData.otherSubject.trim()
    : DEPARTMENT_OPTIONS.find(d => d.value === formData.department)?.label ?? '';

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!validateName(formData.name).isValid) {
      newErrors.name = t.form.name.required;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = !formData.email.trim() ? t.form.email.required : t.form.email.invalid;
    }

    if (!formData.department) {
      newErrors.department = t.form.department.required;
    }

    if (isOther && !formData.otherSubject.trim()) {
      newErrors.otherSubject = t.form.otherSubject.required;
    }

    if (!validateMessage(formData.message).isValid) {
      newErrors.message = !formData.message.trim() ? t.form.message.required : t.form.message.maxLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const callNotifyFunction = async (data: {
    full_name: string;
    email: string;
    message: string;
    department: string;
    subject: string;
  }) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        console.warn('Failed to send notification email:', response.status);
      }
    } catch (error) {
      console.warn('Error calling notify-contact function:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submittedMs = Date.now() - startTime;
    if (isSpamSubmission(formData.honeypot, submittedMs)) {
      setErrors({ general: t.errors.spam });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const { error: insertError } = await supabase
        .from('contact_requests')
        .insert({
          full_name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          message: formData.message.trim(),
        });

      if (insertError) {
        console.error('Database insert error:', insertError);
        setErrors({ general: t.errors.generic });
        return;
      }

      callNotifyFunction({
        full_name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        message: formData.message.trim(),
        department: formData.department,
        subject: resolvedSubject,
      }).catch(() => {});

      setIsSuccess(true);
      setFormData({ name: '', email: '', department: '', otherSubject: '', message: '', honeypot: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ general: t.errors.generic });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-teal-500" />
        </div>
        <h3 className="text-2xl font-semibold text-slate-900 mb-4">{t.success.title}</h3>
        <p className="text-gray-600 mb-6">{t.success.message}</p>
        <button
          onClick={() => setIsSuccess(false)}
          className="text-teal-500 hover:text-teal-600 font-medium transition-colors duration-200"
        >
          {t.success.sendAnother}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t.form.name.label} *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans ${
              errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={t.form.name.placeholder}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <p id="name-error" className="mt-2 text-sm text-red-600" role="alert">{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t.form.email.label} *
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={t.form.email.placeholder}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">{errors.email}</p>
          )}
        </div>

        {/* Department dropdown — RTL-safe via insetInlineEnd logical property */}
        <div>
          <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
            {t.form.department.label} *
          </label>
          <div className="relative">
            <select
              id="department"
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              className={`w-full appearance-none px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans bg-white cursor-pointer ${
                errors.department ? 'border-red-300 bg-red-50' : 'border-gray-300'
              } ${!formData.department ? 'text-gray-400' : 'text-gray-900'}`}
              aria-invalid={!!errors.department}
              aria-describedby={errors.department ? 'department-error' : undefined}
            >
              <option value="" disabled>{t.form.department.placeholder}</option>
              {DEPARTMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {isRtl ? opt.labelAr : opt.label}
                </option>
              ))}
            </select>
            <span
              className="pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400"
              style={{ insetInlineEnd: '1rem' }}
            >
              <ChevronDown className="w-5 h-5" />
            </span>
          </div>
          {errors.department && (
            <p id="department-error" className="mt-2 text-sm text-red-600" role="alert">{errors.department}</p>
          )}
        </div>

        {/* Other subject — only when "Other" selected */}
        {isOther && (
          <div>
            <label htmlFor="otherSubject" className="block text-sm font-medium text-gray-700 mb-2">
              {t.form.otherSubject.label} *
            </label>
            <input
              type="text"
              id="otherSubject"
              value={formData.otherSubject}
              onChange={(e) => handleInputChange('otherSubject', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans ${
                errors.otherSubject ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder={t.form.otherSubject.placeholder}
              maxLength={200}
              aria-invalid={!!errors.otherSubject}
              aria-describedby={errors.otherSubject ? 'otherSubject-error' : undefined}
            />
            {errors.otherSubject && (
              <p id="otherSubject-error" className="mt-2 text-sm text-red-600" role="alert">{errors.otherSubject}</p>
            )}
          </div>
        )}

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            {t.form.message.label} *
          </label>
          <textarea
            id="message"
            rows={6}
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans resize-vertical ${
              errors.message ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={t.form.message.placeholder}
            maxLength={2000}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? 'message-error' : 'message-help'}
          />
          <div className="mt-2 flex justify-between items-start">
            <div>
              {errors.message && (
                <p id="message-error" className="text-sm text-red-600" role="alert">{errors.message}</p>
              )}
            </div>
            <p id="message-help" className="text-sm text-gray-500">{formData.message.length}/2000</p>
          </div>
        </div>

        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
          <label htmlFor="website">Website (leave blank)</label>
          <input
            type="text"
            id="website"
            name="website"
            value={formData.honeypot}
            onChange={(e) => handleInputChange('honeypot', e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* General error */}
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600" role="alert">{errors.general}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 font-sans flex items-center justify-center gap-2"
        >
          <Send className="w-5 h-5" />
          <span>{isSubmitting ? t.form.submitting : t.form.submit}</span>
        </button>

      </form>
    </div>
  );
}
